import React from "react";
import { Paper, Typography, IconButton, Tooltip } from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import classes from "./myteam.module.css";
import { ImageUrl } from "../../../api-calls/api-urls";
import { formatBytes } from "../../../utilities/commonFunctions";
import { decodeFileName } from "../../../utilities/fileName";
import { DeleteBinIcons } from "../../../components/Icons";

// Badge colours per file type (bg / fg from the Figma Files card)
const BADGE_COLORS = {
  PDF: { bg: "#FEE5E5", fg: "#DC2626" },
  JPG: { bg: "#FEF2CC", fg: "#D0830E" },
  JPEG: { bg: "#FEF2CC", fg: "#D0830E" },
  PNG: { bg: "#D4F2E5", fg: "#089868" },
  DOC: { bg: "#DBE6FD", fg: "#256FE8" },
  DOCX: { bg: "#DBE6FD", fg: "#256FE8" },
  XLS: { bg: "#E1F2E7", fg: "#107C41" },
  XLSX: { bg: "#E1F2E7", fg: "#107C41" },
};
const DEFAULT_BADGE = { bg: "#F3F4F6", fg: "#6B7280" };

const MIME_EXTENSIONS = {
  "application/pdf": "PDF",
  "image/jpeg": "JPG",
  "image/png": "PNG",
  "application/msword": "DOC",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
    "DOCX",
  "application/vnd.ms-excel": "XLS",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
    "XLSX",
};

const getExtension = (doc) => {
  const name = doc?.originalname || "";
  const dot = name.lastIndexOf(".");
  if (dot > 0 && dot < name.length - 1) {
    return name.slice(dot + 1).toUpperCase().slice(0, 4);
  }
  if (MIME_EXTENSIONS[doc?.mimetype]) return MIME_EXTENSIONS[doc.mimetype];
  return doc?.mimetype?.split("/")[1]?.toUpperCase().slice(0, 4) || "FILE";
};

const isAbsolute = (url) => !!url && /^https?:\/\//.test(url);

// S3 uploads store a full URL in location/destination; older local uploads
// store a relative folder in destination that is served from ImageUrl.
export const getDocumentUrl = (doc) => {
  if (isAbsolute(doc?.location)) return doc.location;
  if (isAbsolute(doc?.destination)) {
    return doc.destination.endsWith(doc.filename || "")
      ? doc.destination
      : `${doc.destination.replace(/\/$/, "")}/${doc.filename}`;
  }
  const base = ImageUrl.replace(/\/$/, "");
  let dest = doc?.destination || "uploads";
  if (!dest.startsWith("/")) dest = `/${dest}`;
  if (!dest.endsWith("/")) dest = `${dest}/`;
  return `${base}${dest}${doc?.filename || ""}`;
};

// onOpen lets the parent show its own preview; without it the file opens
// in a new tab.
const MemberFilesCard = ({ documents = [], ln = {}, onDelete, onOpen }) => {
  const openDocument = (doc) => {
    if (onOpen) {
      onOpen(doc);
      return;
    }
    window.open(getDocumentUrl(doc), "_blank", "noopener,noreferrer");
  };

  return (
    <Paper className={classes.filesCard} elevation={0}>
      <div className={classes.filesCardHeader}>
        <Typography className={classes.filesCardTitle}>
          {ln.files || "Files"}
        </Typography>
        <Typography className={classes.filesCardCount}>
          {`${documents.length} ${ln.uploaded || "uploaded"}`}
        </Typography>
      </div>

      {documents.length === 0 ? (
        <Typography className={classes.filesCardEmpty}>
          {ln.no_files_uploaded || "No files uploaded yet"}
        </Typography>
      ) : (
        documents.map((doc, index) => {
          const ext = getExtension(doc);
          const badge = BADGE_COLORS[ext] || DEFAULT_BADGE;
          return (
            <div
              key={doc._id || index}
              className={classes.fileRow}
              role="button"
              tabIndex={0}
              onClick={() => openDocument(doc)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  openDocument(doc);
                }
              }}
            >
              <div
                className={classes.fileBadge}
                style={{ backgroundColor: badge.bg, color: badge.fg }}
              >
                {ext}
              </div>
              <div className={classes.fileInfo}>
                <Typography className={classes.fileName} title={decodeFileName(doc.originalname)}>
                  {decodeFileName(doc.originalname) || ln.document || "Document"}
                </Typography>
                {doc.size ? (
                  <Typography className={classes.fileSize}>
                    {formatBytes(doc.size)}
                  </Typography>
                ) : null}
              </div>
              {onDelete && (
                <Tooltip title={ln.delete || "Delete"}>
                  <IconButton
                    size="small"
                    className={classes.fileDeleteButton}
                    onClick={(e) => {
                      e.stopPropagation();
                      onDelete(doc);
                    }}
                  >
                    <DeleteBinIcons />
                  </IconButton>
                </Tooltip>
              )}
              <span className={classes.fileChevron}>
                <ChevronRightIcon sx={{ fontSize: 16, color: "#282C34" }} />
              </span>
            </div>
          );
        })
      )}
    </Paper>
  );
};

export default MemberFilesCard;
